import {Button, IconButton, TextInput, Textarea} from '@primer/react'
import {ChevronLeftIcon, ChevronRightIcon} from '@primer/octicons-react'
import {useState, useCallback, useRef, useEffect} from 'react'
import {clsx} from 'clsx'
import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import type {RequestedSchema, RequestedSchemaProperty} from '@github-ui/copilot-shared-ui/ahp/interactive-prompt-types'
import {ChoiceButton, ChoiceButtonGroup} from '@github-ui/agent-sessions-interface/components/ChoiceButton'
import {UserChoiceCard} from '@github-ui/agent-sessions-interface/components/UserChoiceCard'
import choiceStyles from '@github-ui/agent-sessions-interface/components/ChoiceButton.module.css'
import styles from './ElicitationAskUserDisplay.module.css'

export type ElicitationSubmitContent = Record<string, string | string[]>

export interface ElicitationAskUserDisplayProps {
  message: string
  schema: RequestedSchema
  onElicitationSubmit: (content: ElicitationSubmitContent) => void
  onDecline?: () => void
  disabled: boolean
  /** When true, hides container chrome (for embedding in another container) */
  embedded?: boolean
}

export function ElicitationAskUserDisplay({
  message,
  schema,
  onElicitationSubmit,
  onDecline,
  disabled,
  embedded = false,
}: ElicitationAskUserDisplayProps) {
  const entries = Object.entries(schema.properties)
  const totalSteps = entries.length
  const isMultiStep = totalSteps > 1
  const [currentStep, setCurrentStep] = useState(0)
  const [selections, setSelections] = useState<Record<string, string | string[]>>(() => {
    const defaults: Record<string, string | string[]> = {}
    for (const [key, prop] of entries) {
      if (prop.multiSelect) {
        defaults[key] = []
      } else if (prop.default) {
        defaults[key] = prop.default
      }
    }
    return defaults
  })
  const [freeformValues, setFreeformValues] = useState<Record<string, string>>({})
  const freeformOnlyKeys = new Set(
    entries.filter(([, prop]) => !prop.enum || prop.enum.length === 0).map(([key]) => key),
  )
  const [freeformSelectedSteps, setFreeformSelectedSteps] = useState<Set<string>>(() => new Set(freeformOnlyKeys))

  const [currentKey] = entries[currentStep] ?? []
  const isFreeformSelected = currentKey ? freeformSelectedSteps.has(currentKey) : false
  const currentFreeformValue = currentKey ? (freeformValues[currentKey] ?? '') : ''

  const setSelection = useCallback((key: string, value: string) => {
    setSelections(prev => ({...prev, [key]: value}))
    setFreeformSelectedSteps(prev => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }, [])

  const toggleMultiSelection = useCallback((key: string, value: string) => {
    setSelections(prev => {
      const current = prev[key]
      const arr = Array.isArray(current) ? current : []
      const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]
      return {...prev, [key]: next}
    })
    setFreeformSelectedSteps(prev => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }, [])

  const handleSubmit = useCallback(() => {
    if (disabled) return
    const finalSelections: ElicitationSubmitContent = {...selections}
    for (const key of freeformSelectedSteps) {
      const freeVal = freeformValues[key]?.trim()
      if (freeVal) {
        finalSelections[key] = freeVal
      }
    }
    onElicitationSubmit(finalSelections)
  }, [disabled, onElicitationSubmit, selections, freeformSelectedSteps, freeformValues])

  const goPrev = useCallback(() => setCurrentStep(prev => Math.max(0, prev - 1)), [])
  const goNext = useCallback(() => setCurrentStep(prev => Math.min(totalSteps - 1, prev + 1)), [totalSteps])

  const requiredFields = schema.required ?? []
  const allRequiredFilled = requiredFields.every(key => {
    if (freeformSelectedSteps.has(key)) {
      return (freeformValues[key]?.trim() ?? '').length > 0
    }
    const sel = selections[key]
    if (Array.isArray(sel)) return sel.length > 0
    return !!sel
  })
  // Block submit if any explicitly-toggled "Other" field has empty input.
  // Freeform-only fields (no enum) are excluded — they're handled by allRequiredFilled when required,
  // and should not block submit when optional.
  const hasFreeformWithEmptyInput = [...freeformSelectedSteps].some(
    key => !freeformOnlyKeys.has(key) && (freeformValues[key]?.trim() ?? '').length === 0,
  )
  const isSubmitDisabled = disabled || !allRequiredFilled || hasFreeformWithEmptyInput

  const isLastStep = currentStep >= totalSteps - 1
  const currentProp = entries[currentStep]?.[1]
  const enumCount = currentProp?.enum?.length ?? 0
  const isFreeformOnly = enumCount === 0
  const currentLabel = currentProp ? (currentProp.title ?? currentKey ?? '') : ''

  return (
    <UserChoiceCard
      title={<MarkdownRenderer markdown={message} />}
      embedded={embedded}
      headerButtons={
        isMultiStep ? (
          <SchemaStepIndicator current={currentStep} total={totalSteps} onPrev={goPrev} onNext={goNext} />
        ) : undefined
      }
      previewContent={
        currentKey && currentProp ? (
          <div className={styles.previewText}>
            <p className={styles.fieldTitle}>
              {currentLabel}
              {requiredFields.includes(currentKey) && <span className={styles.required}> *</span>}
            </p>
            {currentProp.description && <p className={styles.fieldDescription}>{currentProp.description}</p>}
          </div>
        ) : undefined
      }
      footerButtons={
        <>
          {onDecline && (
            <Button variant="default" onClick={onDecline} disabled={disabled}>
              Skip
            </Button>
          )}
          {isMultiStep && currentStep > 0 && (
            <Button variant="default" onClick={goPrev} disabled={disabled}>
              Previous question
            </Button>
          )}
          {isLastStep ? (
            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitDisabled}>
              Submit
            </Button>
          ) : (
            <Button variant="primary" onClick={goNext} disabled={disabled}>
              Next question
            </Button>
          )}
        </>
      }
    >
      {currentKey && currentProp && isFreeformOnly ? (
        <FreeformTextarea
          value={currentFreeformValue}
          onChange={val => setFreeformValues(prev => ({...prev, [currentKey]: val}))}
          onSubmit={isLastStep ? handleSubmit : goNext}
          disabled={disabled}
          placeholder="Type your answer..."
          label={currentLabel}
        />
      ) : (
        currentKey &&
        currentProp && (
          <ChoiceButtonGroup>
            <SchemaField
              property={currentProp}
              selectedValue={isFreeformSelected ? undefined : selections[currentKey]}
              onSelect={value =>
                currentProp.multiSelect ? toggleMultiSelection(currentKey, value) : setSelection(currentKey, value)
              }
              startNumber={1}
              disabled={disabled}
            />
            <FreeformChoiceRow
              number={enumCount + 1}
              selected={isFreeformSelected}
              value={currentFreeformValue}
              onToggle={() => {
                setFreeformSelectedSteps(prev => {
                  const next = new Set(prev)
                  if (next.has(currentKey)) {
                    next.delete(currentKey)
                  } else {
                    next.add(currentKey)
                  }
                  return next
                })
              }}
              onChange={val => setFreeformValues(prev => ({...prev, [currentKey]: val}))}
              onSubmit={handleSubmit}
              disabled={disabled}
            />
          </ChoiceButtonGroup>
        )
      )}
    </UserChoiceCard>
  )
}

interface SchemaStepIndicatorProps {
  current: number
  total: number
  onPrev: () => void
  onNext: () => void
}

function SchemaStepIndicator({current, total, onPrev, onNext}: SchemaStepIndicatorProps) {
  return (
    <div className={styles.stepIndicator}>
      <IconButton
        icon={ChevronLeftIcon}
        aria-label="Previous step"
        size="small"
        variant="invisible"
        onClick={onPrev}
        disabled={current === 0}
      />
      <span className={styles.stepLabel}>
        {current + 1} of {total}
      </span>
      <IconButton
        icon={ChevronRightIcon}
        aria-label="Next step"
        size="small"
        variant="invisible"
        onClick={onNext}
        disabled={current >= total - 1}
      />
    </div>
  )
}

interface SchemaFieldProps {
  property: RequestedSchemaProperty
  selectedValue?: string | string[]
  onSelect: (value: string) => void
  startNumber: number
  disabled: boolean
}

function SchemaField({property, selectedValue, onSelect, startNumber, disabled}: SchemaFieldProps) {
  const enumValues = property.enum ?? []
  const enumLabels = property.enumLabels
  const selectedSet = Array.isArray(selectedValue) ? new Set(selectedValue) : null

  return (
    <>
      {enumValues.map((value, i) => (
        <ChoiceButton
          key={value}
          number={startNumber + i}
          label={enumLabels?.[value] ?? value}
          isRecommended={value === property.default}
          disabled={disabled}
          showCheckbox
          checked={selectedSet ? selectedSet.has(value) : selectedValue === value}
          onClick={() => onSelect(value)}
        />
      ))}
    </>
  )
}

interface FreeformChoiceRowProps {
  number: number
  selected: boolean
  value: string
  onToggle: () => void
  onChange: (value: string) => void
  onSubmit: () => void
  disabled: boolean
}

function FreeformChoiceRow({number, selected, value, onToggle, onChange, onSubmit, disabled}: FreeformChoiceRowProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (selected) {
      inputRef.current?.focus()
    }
  }, [selected])

  return (
    <div
      className={clsx(choiceStyles.Row, choiceStyles.inputRow)}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={e => {
        if (!(e.target as HTMLElement).closest('input')) {
          onToggle()
        }
      }}
      onKeyDown={e => {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.key === 'Enter' && !(e.target as HTMLElement).closest('input')) {
          onToggle()
        }
      }}
    >
      <span className={choiceStyles.NumberBadge}>{number}</span>
      <span className={clsx(choiceStyles.Checkbox, selected && choiceStyles.checkboxChecked)} aria-hidden="true" />
      {selected ? (
        <TextInput
          ref={inputRef}
          className={styles.freeformInput}
          placeholder="Type your answer..."
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => {
            // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault()
              onSubmit()
            }
          }}
          disabled={disabled}
          size="small"
          aria-label="Freeform answer"
        />
      ) : (
        <div className={choiceStyles.Text}>
          <div className={choiceStyles.Title}>Other (type your answer)</div>
        </div>
      )}
    </div>
  )
}

interface FreeformTextareaProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  disabled: boolean
  placeholder: string
  label: string
}

function FreeformTextarea({value, onChange, onSubmit, disabled, placeholder, label}: FreeformTextareaProps) {
  return (
    <Textarea
      className={styles.freeformTextarea}
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => {
        // Don't submit on the Enter that confirms an IME composition (e.g. Japanese kanji).
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault()
          onSubmit()
        }
      }}
      placeholder={placeholder}
      disabled={disabled}
      rows={3}
      resize="vertical"
      block
      aria-label={label}
    />
  )
}
